/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { AnyAction } from 'redux';
import { VU } from '@tivocorp/tivo-webapp-loggers';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { Channel } from '../../../core/entities/Channel';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { startVideoPlayback } from '../../videoplayer/videoplayer.reducers';
import { setChannelOptionsAction, setChannelsTypeAction } from './channeloptionsoverlay.actions';
import { SET_CHANNELS_TYPE, SET_CHANNEL_OPTIONS } from './channeloptionsoverlay.types';
import { Storage } from '../../../core/storage/Storage';
import { loadGridGuide } from '../../guide/guide.reducers';
import { setVerticalPageCount } from '../../guide/guidehighlight/guidehighlight.reducers';
import { OverlayTypes } from '../OverlayTypes';
import { setOverlayHighlightHorizontalPositionAction } from '../overlayhighlight/overlayhighlight.actions';
import { setGridGuideOffsetAction } from '../../guide/guide.actions';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { VuLogger } from '../../../core/logging/VULogger';
import { GuideCell } from '../../../core/entities/GuideCell';
import { GridGuideRow } from '../../../core/entities/GridGuideRow';
import { InteractiveComponents } from '../../../core/enums/InteractiveComponents';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import {
    hideOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    showOverlayDialog,
} from '../../overlaydialog/overlaydialog.reducers';
import { PlaybackContentType } from '../../../core/socu/enums/PlaybackContentType';
import { SocuDataModelImpl } from '../../../core/socu/impls/SocuDataModelImpl';
import { VideoManager } from '../../../core/videoplayer';
import { QoEAppLogger } from '../../../core/logging';

const initialState: {
    channelsTypeIndex: string;
    channelsType: string;
    watchNowText: string;
    channelsText: string;
    horizontalSelectionOptions: string[];
} = {
    channelsTypeIndex:
        Storage.getInstance().getItem(Storage.Keys.CHANNELS_TYPE) ||
        LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS,
    channelsType: '',
    watchNowText: '',
    channelsText: '',
    horizontalSelectionOptions: [],
};
const GLOBAL_MOUSE_TRAP: string = 'HideChannelOptionsOverlay';

const channelOptionsOverlayReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SET_CHANNELS_TYPE:
            return {
                ...state,
                channelsType: payload.channelsType,
                channelsTypeIndex: payload.channelsTypeIndex,
            };
        case SET_CHANNEL_OPTIONS:
            return {
                ...state,
                channelsType: payload.channelsType,
                watchNowText: payload.watchNowText,
                channelsText: payload.channelsText,
                horizontalSelectionOptions: payload.horizontalSelectionOptions,
            };
        default:
            return state;
    }
};

export const showChannelOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { guideHighlight, channelOptionsOverlay, gridGuide } = getState();
    const { channelsTypeIndex, watchNowText, channelsText, horizontalSelectionOptions } =
        channelOptionsOverlay;
    const { verticalPosition } = guideHighlight;
    const { channels } = gridGuide;
    const mHorizontalSelectionOptionIndex = [
        LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS,
        LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS,
    ];
    const selectedChannelsTypeIndex = mHorizontalSelectionOptionIndex.indexOf(channelsTypeIndex);
    dispatch(setOverlayHighlightHorizontalPositionAction(selectedChannelsTypeIndex));

    const { channelNumber, affiliate, callSign, channelLogoUrl } =
        channels && Object(channels[verticalPosition]);
    const overlayDialogProps: IOverlayDialog = {
        enableCaret: true,
        hasDimmer: true,
        hasSeperator: false,
        iconURL: channelLogoUrl,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: [
            { text: watchNowText },
            {
                text: channelsText,
                horizontalSelectionoptions: horizontalSelectionOptions,
            },
        ],
        overlayType: OverlayTypes.CHANNEL_OPTIONS,
        subtitle: `${channelNumber} ${affiliate || callSign}`,
        title: Locale.getString(LocaleKeys.GUIDE_CHANNEL_OPTIONS_OVERLAY_TITLE),
        verticalPositionCaretAvailable: [1],
    };
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY));
    dispatch(showOverlayDialog(overlayDialogProps));
};

export const onKeyOkChannelOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const {
        verticalPosition: overlayHighlightVerticalPosition,
        horizontalPosition: overlayHighlightHorizontalPosition,
    } = getState().overlayHighlight;

    if (overlayHighlightVerticalPosition === 0) {
        const { gridGuide, guideHighlight } = getState();
        const { verticalPosition } = guideHighlight;
        const { channels, guideRows, isPastGuideMode, futureGuideRows } = gridGuide;
        const guideRowsData: GridGuideRow[] = isPastGuideMode ? futureGuideRows : guideRows;
        const guideCells: GuideCell[] = guideRowsData[verticalPosition]?.guideCells;
        const channel: Channel = channels[verticalPosition];
        if (channel) {
            VuLogger.createPayloadForWatchNow(guideCells, channel).then((payload) => {
                VU.itemSelectedEvent(payload);
            });
            if (channel.channelNumber !== QoEAppLogger.lastTunedChannel) {
                QoEAppLogger.setWatchContentEndReason(
                    QoEAppLogger.PlaybackEndReason
                        .USER_MOVE_TO_LIVE_TV_FROM_GUIDE_AND_CHANNEL_WAS_CHANGED,
                );  
            }
            if (channel.channelNumber !== VideoManager.getInstance().getPlayable()?.channel?.channelNumber) {
                VuLogger.logWatchStoppedEvent(
                    VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE
                );
            }
            dispatch(hideOverlayDialog());
            const socuModelImpl = SocuDataModelImpl.getInstance();
            if (socuModelImpl.isSocu()) {
                VideoManager.getInstance().updateLastViewedPosition();
                socuModelImpl.resetSocuData();
            }
            dispatch(startVideoPlayback(channel));
        }
    } else {
        const { channelsTypeIndex } = getState().channelOptionsOverlay;
        const mHorizontalSelectionOptionIndex = [
            LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS,
            LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS,
        ];
        const selectedChannelsTypeIndex =
            mHorizontalSelectionOptionIndex[overlayHighlightHorizontalPosition];
        if (channelsTypeIndex === selectedChannelsTypeIndex) {
            dispatch(hideOverlayDialog());
        } else {
            VuLogger.logChannelOptionOverlayOkUserAction(selectedChannelsTypeIndex);
            Storage.getInstance().setItem(Storage.Keys.CHANNELS_TYPE, selectedChannelsTypeIndex);
            dispatch(
                setChannelsTypeAction(
                    Locale.getString(LocaleKeys[selectedChannelsTypeIndex]),
                    selectedChannelsTypeIndex,
                ),
            );
            dispatch(setVerticalPageCount(0));
            dispatch(setGridGuideOffsetAction(0));
            dispatch(loadGridGuide());
            dispatch(hideOverlayDialog());
        }
    }
};

export const onKeyPlayChannelOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;
    if (overlayHighlightVerticalPosition > 0) return;
    dispatch(onKeyOkChannelOptionsOverlay());
};

export const onKeyBackChannelOptionsOverlay = () => (dispatch: Function) => {
    VuLogger.logChannelOptionOverlayCancelUserAction();
    dispatch(hideOverlayDialog());
};

export const setChannelOptions = () => (dispatch: Function) => {
    const watchNowText = Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_NOW_TEXT);
    const channelsText = Locale.getString(LocaleKeys.OVERLAY_OPTIONS_CHANNELS_TEXT);
    const horizontalSelectionOptions = [
        Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS),
        Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS),
    ];
    const mChannelsType =
        Locale.getString(Storage.getInstance().getItem(Storage.Keys.CHANNELS_TYPE)) ||
        Locale.getString(LocaleKeys.GUIDE_CHANNEL_TYPES_MY_CHANNELS);

    dispatch(
        setChannelOptionsAction(
            mChannelsType,
            watchNowText,
            channelsText,
            horizontalSelectionOptions,
        ),
    );
};

export const onMouseClickChannelOptionsOverlay = () => (dispatch: Function, getState: Function) => {
    const { activeComponent } = getState().interactiveLayer;
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }

    if (activeComponent === InteractiveComponents.WEST) dispatch(onKeyLeftOverlayDialog());
    else if (activeComponent === InteractiveComponents.EAST) dispatch(onKeyRightOverlayDialog());
    else dispatch(onKeyOkChannelOptionsOverlay());
};

export default channelOptionsOverlayReducer;
